/**
 * Top level react component where we plug in global event handlers
 */
import React, { Component } from 'react';
import _ from 'lodash';
import { SEND_CLIENTSIDE_FPTI_ACTION, DOM_EVENT } from '../../store/FPTI/FPTIConstants';
import { connect } from 'react-redux';
import { matchesSelector } from '../AppUtils';

import PropTypes from 'prop-types';

const CLICK_FPTI_ELEMENTS = ['input[type=submit]', 'input[type=button]', 'button', 'a'];
const FOCUS_FPTI_ELEMENTS = ['input[type=text]', 'input[type=tel]', 'input[type=number]', 'input[type=password]', 'input[type=email]'];
const BLUR_FPTI_ELEMENTS = ['input[type=text]', 'input[type=tel]', 'input[type=number]', 'input[type=password]', 'input[type=email]', 'select'];
const CHANGE_FPTI_ELEMENTS = ['input[type=checkbox]', 'input[type=radio]'];

// Extract important information from dom event target and return it in a simple javascript object
function domTargetToObject(target) {
    if (!target) {
        return {};
    }

    const output = _.pick(target, ['id', 'name']);
    output.tagName = target.tagName.toLowerCase();
    output.type = target.type && target.type.toLowerCase();

    if (target.getAttribute('aria-expanded')) {
        output.expanded = target.getAttribute('aria-expanded') === 'true';
    }
    // use getAttribute for cross-browser support
    if (target.getAttribute('data-haserror') === 'true') {
        output.hasError = true;
        output.errorMessageVisible = (target.getAttribute('data-errorshowmsg') === 'true');
        output.errorMessage = target.getAttribute('data-errormsg');
    }

    // extract element specific data
    switch (output.tagName) {
        case 'input':
            if (output.type === 'checkbox') {
                output.value = target.checked;
            } else {
                output.value = target.value;
            }
            break;
        case 'a':
            output.role = target.getAttribute('role');
            output.href = target.href;
            output.value = target.value || target.textContent;
            break;
        case 'button':
            output.value = target.value || target.textContent;
            if (_.get(target, 'classList.contains') && target.classList.contains('link')) {
                output.role = 'link';
            }
            break;
        case 'select':
            if (_.isString(target.value)) {
                output.value = target.value;
            } else {
                output.value = target.textContent;
            }
            break;
    }

    return output;
}

/**
 * This class "plugs" into the top level react div with the data-reactroot attribute.
 * Since it's at the top of the DOM tree for all react (only the App class is higher)
 *  it's a good place to plug in event handlers
 */
export class DataReactRoot extends Component {
    static propTypes = {
        children: PropTypes.element,
        pageName: PropTypes.string.isRequired,
        dispatchFPTIAction: PropTypes.func.isRequired
    };

    dispatchFpti = (eventType, target) => {
        const { pageName, dispatchFPTIAction } = this.props;
        /**
         * extract target information prior to any processing such as validation
         * we can use this for comparing how validation or normalization modified user's input
         */
        const targetObjectOnCapture = domTargetToObject(target);

        // Timeout 0 used to delay reading DOM until validation is processed
        setTimeout(() => {
            const targetObject = domTargetToObject(target);
            const action = {
                type: SEND_CLIENTSIDE_FPTI_ACTION,
                payload: {
                    pageName,
                    eventType,
                    targetObjectOnCapture,
                    targetObject
                }
            };
            dispatchFPTIAction(action);
        }, 0);
    };

    onClickCapture = e => {
        if (matchesSelector(e.target, CLICK_FPTI_ELEMENTS.join(','))) {
            this.dispatchFpti(DOM_EVENT.CLICK, e.target);
        } else if (matchesSelector(e.target.parentNode, CLICK_FPTI_ELEMENTS)) {
            // sometimes inner node is the initial target, like <button><span>button text</span></button>
            this.dispatchFpti(DOM_EVENT.CLICK, e.target.parentNode);
        }
    };

    onFocusCapture = e => {
        if (matchesSelector(e.target, FOCUS_FPTI_ELEMENTS.join(','))) {
            this.dispatchFpti(DOM_EVENT.FOCUS, e.target);
        }
    };

    onBlurCapture = e => {
        if (matchesSelector(e.target, BLUR_FPTI_ELEMENTS.join(','))) {
            this.dispatchFpti(DOM_EVENT.BLUR, e.target);
        }
    };

    onChangeCapture = e => {
        if (matchesSelector(e.target, CHANGE_FPTI_ELEMENTS.join(','))) {
            this.dispatchFpti(DOM_EVENT.CHANGE, e.target);
        }
    };

    render() {
        return (
            // use capture phase handlers to preempt preventDefault scenarios on bubble up phase
            <div className="data-reactRoot cape-position"
                onClickCapture={this.onClickCapture}
                onFocusCapture={this.onFocusCapture}
                onBlurCapture={this.onBlurCapture}
                onChangeCapture={this.onChangeCapture}
            >
                {this.props.children}
            </div>
        );
    }
}

export const mapStateToProps = state => ({
    pageName: _.get(state, 'skeletonPage.currentPage')
});

export const mapDispatchToProps = dispatch => ({
    dispatchFPTIAction(action) {
        dispatch(action);
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(DataReactRoot);
